// render-host/otel.js — chrome-side OpenTelemetry scaffold (SPEC-chrome-otel-spans-v0 #800).
// Inv-4: the SAME instrumentation calls serve production OTel and the tests — no `if testMode`.
// The swap point is the EXPORTER: tests register the in-memory one, production a collector.

import { context, trace } from '@opentelemetry/api';
import {
    BasicTracerProvider,
    SimpleSpanProcessor,
    InMemorySpanExporter,
} from '@opentelemetry/sdk-trace-base';
// context-async-hooks is DYNAMIC-imported under the Node guard below: a static import makes esbuild
// pull its Node built-ins into the browser bundle and analysis fails on `async_hooks`.

const TRACER_NAME = 'cog.chrome';

export function getTracer() {
    return trace.getTracer(TRACER_NAME);
}

export async function installInMemoryExporter() {
    const exporter = new InMemorySpanExporter();
    const provider = new BasicTracerProvider({
        spanProcessors: [new SimpleSpanProcessor(exporter)],
    });
    trace.setGlobalTracerProvider(provider);
    // Required for parent propagation (Inv-2): without one, context.with() is a no-op.
    if (typeof process !== 'undefined' && process.versions && process.versions.node) {
        const { AsyncHooksContextManager } = await import('@opentelemetry/context-async-hooks');
        context.setGlobalContextManager(new AsyncHooksContextManager().enable());
    }
    return exporter;
}

export function resetTracerProvider() {
    trace.disable();
}

// installBufferingExporter — production's exporter: spans go into a BOUNDED local ring and stay
// there until drained, which is the only way to answer "what happened before the panel painted".
// Inv-4 holds: instrumentation calls are unchanged, only the exporter swaps.
export function installBufferingExporter(buffer) {
    const exporter = {
        export(spans, resultCallback) {
            for (const s of spans) {
                const ctx = typeof s.spanContext === 'function' ? s.spanContext() : {};
                buffer.record({
                    name: s.name,
                    traceId: ctx.traceId,
                    spanId: ctx.spanId,
                    startTimeMs: Array.isArray(s.startTime)
                        ? s.startTime[0] * 1e3 + s.startTime[1] / 1e6 : undefined,
                    durationMs: Array.isArray(s.duration)
                        ? s.duration[0] * 1e3 + s.duration[1] / 1e6 : undefined,
                    attributes: s.attributes || {},
                    status: s.status,
                });
            }
            // Never report failure: the SDK would retry, and a retry storm in the page we are
            // measuring is worse than a lost span.
            resultCallback({ code: 0 });
        },
        shutdown() { return Promise.resolve(); },
        forceFlush() { return Promise.resolve(); },
    };
    const provider = new BasicTracerProvider({
        spanProcessors: [new SimpleSpanProcessor(exporter)],
    });
    trace.setGlobalTracerProvider(provider);
    return exporter;
}
